import { useState, useContext, useMemo } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useNavigate, Link } from "react-router-dom";
import { UserContext } from "../../context/userContext/userContext";
import { useStyle } from "../../context/styleContext/styleContext";
import TermsModal from "../Terms/TermsModal";
import Terms from "../Terms/Terms";
import Seo from "../../components/Seo";

import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

export default function Login() {
  const navigate = useNavigate();
  const { style } = useStyle();
  const { setUser, setUpdateFlag, isLoading } = useContext(UserContext);

  const [isModalOpen, setModalOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [data, setData] = useState({
    email: "",
    password: "",
  });

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const emailIsValid = useMemo(() => {
    if (!data.email) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim());
  }, [data.email]);

  const canSubmit =
    data.email.trim() &&
    emailIsValid &&
    data.password &&
    !submitting &&
    !isLoading;

  const navToRegister = () => {
    navigate("/register");
  };

  const setField = (field, value) => {
    setData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const sharedInputSx = {
    "& .MuiInputBase-root": {
      color: style.color4,
      backgroundColor: style.color2,
      boxShadow: "0 8px 18px rgba(0,0,0,0.18)",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: style.color3,
        borderWidth: "1.25px",
      },
      "&:hover fieldset": {
        borderColor: style.color4,
      },
      "&.Mui-focused fieldset": {
        borderColor: style.color4,
        borderWidth: "1.5px",
      },
    },
    "& .MuiInputLabel-root": {
      color: style.color3,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: style.color4,
    },
    "& .MuiInputBase-input": {
      color: style.color4,
      fontSize: "0.95rem",
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
      marginTop: "4px",
      minHeight: "20px",
    },
    "& input:-webkit-autofill": {
      WebkitBoxShadow: `0 0 0 100px ${style.color2} inset !important`,
      WebkitTextFillColor: `${style.color4} !important`,
      caretColor: `${style.color4} !important`,
    },
  };

  const loginUser = async (e) => {
    e.preventDefault();

    const email = data.email.trim();
    const password = data.password;

    if (!email) {
      toast.error("Please enter your email address.");
      return;
    }

    if (!emailIsValid) {
      toast.error("Please enter a valid email address.");
      return;
    }

    if (!password) {
      toast.error("Please enter your password.");
      return;
    }

    try {
      setSubmitting(true);

      const response = await axios.post("/login", {
        email,
        password,
      });

      if (response.data?.error) {
        toast.error(response.data.error);
        return;
      }

      const userData = response.data;

      localStorage.setItem("token", userData.token);
      await setUser(userData);
      await setUpdateFlag();

      toast.success("Login successful!");
      navigate("/");
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.error || "An unexpected error occurred while logging in."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title="Login | Parish Audio"
        description="Login to your Parish Audio account."
        canonical="https://parishaudio.com/login"
        robots="noindex,follow"
      />

      <Box
        sx={{
          minHeight: "100%",
          px: 2,
          py: { xs: 2, md: 3 },
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 500,
            overflow: "hidden",
            borderRadius: "10px",
            background: `linear-gradient(180deg, ${style.color1} 0%, rgba(39,55,77,0.96) 100%)`,
            border: `1px solid ${style.color2}`,
            boxShadow: "0 20px 50px rgba(0,0,0,0.32)",
          }}
        >
          <Box
            sx={{
              px: { xs: 2.25, md: 3 },
              pt: { xs: 2.5, md: 3 },
              pb: 1.5,
              borderBottom: `1px solid rgba(255,255,255,0.08)`,
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))",
            }}
          >
            <Stack spacing={1.25} alignItems="center" textAlign="center">
              <Typography
                variant="h5"
                sx={{
                  color: style.color4,
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                }}
              >
                Login
              </Typography>
            </Stack>
          </Box>

          <Box
            component="form"
            onSubmit={loginUser}
            sx={{
              px: { xs: 2.25, md: 3 },
              py: { xs: 2.25, md: 3 },
            }}
          >
            <Stack spacing={1.25}>
              <TextField
                label="Email Address"
                type="email"
                autoComplete="email"
                fullWidth
                size="small"
                value={data.email}
                onChange={(e) => setField("email", e.target.value)}
                error={!!data.email && !emailIsValid}
                helperText={
                  !!data.email && !emailIsValid ? "Enter a valid email address." : " "
                }
                sx={sharedInputSx}
              />

              <FormControl fullWidth size="small" sx={sharedInputSx}>
                <InputLabel htmlFor="login-password">Password</InputLabel>
                <OutlinedInput
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  value={data.password}
                  autoComplete="current-password"
                  onChange={(e) => setField("password", e.target.value)}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword((show) => !show)}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        sx={{ color: style.color3 }}
                      >
                        {showPassword ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Password"
                />
              </FormControl>

              <Box
                sx={{
                  mt: -0.25,
                  textAlign: "center",
                }}
              >
                <Link
                  to="/forgot-password"
                  style={{
                    fontSize: "0.88rem",
                    fontWeight: 700,
                    color: style.color4,
                    textDecoration: "none",
                  }}
                >
                  Forgot Password?
                </Link>
              </Box>

              <Button
                type="submit"
                disabled={!canSubmit}
                sx={{
                  color: style.color4,
                  fontWeight: 700,
                  margin: "auto",
                  width: "100%",
                  backgroundColor: style.color1,
                  borderWidth: "1px",
                  borderColor: style.color3,
                  filter: "brightness(1.1)",
                  borderStyle: "solid",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: style.color2,
                  },
                  "&.Mui-disabled": {
                    color: "rgba(255,255,255,0.45)",
                    background: "rgba(255,255,255,0.08)",
                    borderColor: "rgba(255,255,255,0.08)",
                  },
                }}
              >
                {submitting ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} sx={{ color: style.color4 }} />
                    <span>Logging in...</span>
                  </Stack>
                ) : (
                  "Login"
                )}
              </Button>

              <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", my: 0.5 }} />

              <Stack spacing={0.75} alignItems="center">
                <Typography sx={{ color: style.color3, fontSize: "0.92rem" }}>
                  Not yet a member?
                </Typography>

                <Button
                  onClick={navToRegister}
                  sx={{
                    px: 2.25,
                    py: 0.9,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.92rem",
                    color: style.color4,
                    backgroundColor: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.10)",
                    },
                  }}
                >
                  Create Account
                </Button>
              </Stack>

              <Box sx={{ pt: 0.5, textAlign: "center" }}>
                <Typography
                  sx={{
                    color: style.color3,
                    fontSize: "0.84rem",
                    lineHeight: 1.5,
                  }}
                >
                  By using this website, you agree to our terms and conditions.
                </Typography>

                <Button
                  onClick={openModal}
                  sx={{
                    mt: 0.15,
                    minWidth: 0,
                    p: 0.25,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.84rem",
                    color: style.color4,
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: style.color3,
                    },
                  }}
                >
                  View terms and conditions
                </Button>
              </Box>
            </Stack>
          </Box>
        </Paper>
      </Box>

      <TermsModal isOpen={isModalOpen} close={closeModal}>
        <Terms />
      </TermsModal>
    </>
  );
}

// import {useState, useContext, useEffect} from "react";
// import axios from 'axios'
// import {toast} from 'react-hot-toast'
// import {useNavigate, Link} from 'react-router-dom'
// import{UserContext} from '../../context/userContext/userContext';
// import { useStyle } from "../../context/styleContext/styleContext";
// import ReactGA from 'react-ga4';
// import TermsModal from "../Terms/TermsModal"
// import Terms from "../Terms/Terms";
// import Button from '@mui/material/Button';
// import Visibility from '@mui/icons-material/Visibility';
// import VisibilityOff from '@mui/icons-material/VisibilityOff';
// import { Checkbox, TextField, FormCOntrol, InputLabel, OutlinedInput, InputAdornment, FormControl, IconButton, FormGroup, FormControlLabel } from "@mui/material";
// import Seo from "../../components/Seo";

// export default function Login(){
//     const navigate = useNavigate()
//     const [isModalOpen, setModalOpen] = useState(false);
//     const openModal = () => setModalOpen(true);
//     const closeModal = () => setModalOpen(false);
//     const { style, setStyle } = useStyle();
//     const [showPassword, setShowPassword] = useState(false);
//   const [showPassword2, setShowPassword2] = useState(false);
//   const [showConfirm, setShowConfirm] = useState(false);
//   const handleClickShowPassword = () => setShowPassword((show) => !show);
//   const handleMouseDownPassword = (event) => {
//     event.preventDefault();
//   };
    

//     const[data,setData] = useState({
//         email: '',
//         password: ''
//     })

//     const navToRegister = () => {
//         navigate('/register')
//       }

//     const { setUser } = useContext(UserContext);
//     const { isLoading,  } = useContext(UserContext);
//     const { setUpdateFlag } = useContext(UserContext);


//     const wait = (n) => new Promise((resolve) => setTimeout(resolve, n));

//     // 
    
//     const loginUser = async (e) => {
//       e.preventDefault();
  
//       const { email, password } = data;
  
//       try {
//           const { data } = await axios.post('/login', {
//               email,
//               password
//           });
  
//           if (data.error) {
//               toast.error(data.error);
//           } else {
//               toast.success("Login successful, welcome!");
//               localStorage.setItem('token', data.token);
//               await setUser(data);
//               //window.location.href = `/`;
//               navigate('/');
              
//           }
//       } catch (error) {
//           // Handle Axios error response
//           if (error.response && error.response.data && error.response.data.error) {
//               toast.error(error.response.data.error);
//           } else {
//               toast.error("An unexpected error occurred while logging in.");
//           }
//       }
//   };

//     return(
//         <><>
//             <Seo
//       title="Login | Parish Audio"
//       description="Login to your Parish Audio account."
//       canonical="https://parishaudio.com/login"
//       robots="noindex,follow"
//     />

//         <div style={{display:'flex', flexDirection:'column', width:'100%', marginBottom:'25px'}}>

// <div style={{display:'flex', flexDirection:'column', width:'90%', maxWidth:'600px', margin:'auto', color:style.color4, textAlign:'center', marginTop:'25px', backgroundColor:style.color1, borderRadius:'10px', borderStyle:'solid', borderColor:style.color1, borderWidth:'5px'}}>
//     <div style={{width:'90%', margin:'auto'}}>

    
//         <div style={{display:'flex', flex:1, textAlign:'center', marginTop:'25px'}}>
//             <h1 style={{flex:1, textAlign:'center', margin:'auto', marginBottom:'20px', marginTop:'10px', fontSize:'2.0em'}}>
//                 Login to your account
//             </h1>
           
//         </div>
//         <div style={{maxWidth:'400px', margin:'auto'}}>
//         <div style={{display:'flex', flex:1, textAlign:'center', minWidth:'300px', margin:'auto', marginBottom:'10px'}}>
//             <h4 style={{flex:1, textAlign:'left', margin:'auto', color:style.color1}}>
//                 <div style={{marginBottom:'5px'}}>
//                 <TextField
//                 id="outlined-basic"
//                 label="Email"
//                 variant="outlined"
//                 value= {data.firstName}
//                 onChange={(e) => setData({...data, email: e.target.value})}
//                 size='small'
//                 fullWidth
//                 sx={{
//                     backgroundColor: style.color2,
//                     borderRadius:'4px',
//                     '& .MuiInputBase-input': {
//                     color: style.color4,  // Change the font color here
//                     fontSize: '16pt',  // Change the font size here
//                     boxShadow:5,
                                                    
//                     },
//                     '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                         borderColor: style.color1, // Change the border color if needed
//                         borderWidth:'2px'
//                     },
//                     '&:hover fieldset': {
//                         borderColor: style.color1, // Change the border color on hover if needed\
//                         borderWidth:'3px',                                                               
                 
                        
//                     },
//                     '&.Mui-focused fieldset': {
//                         borderColor: style.color1, // Change the border color when focused if needed
//                         borderWidth:'3px'
                        
//                     },
//                     '&.Mui-disabled': {
//                         '& fieldset': {
//                         borderColor: style.color1, // Change the border color when disabled
//                         },
//                     },
//                     },
//                     '& .MuiInputLabel-root': {
//                     color: style.color3, // Change the label color here
//                     fontSize:'14pt',
//                     },
//                     '& .MuiInputLabel-root.Mui-focused': {
//                     color: style.color3, // Change the label color when focused here                                 
//                     },
//                     '& .MuiInputLabel-root.Mui-disabled': {
//                     color: style.color2, // Change the label color when disabled
//                     },
//                     '& .MuiInputBase-root.Mui-disabled': {
//                     '& input': {
//                         color: style.color3, // Change the input font color when disabled // so it works in safari
//                         WebkitTextFillColor: style.color4, // so it works on chrome
//                         fontSize:'14pt'
//                     },
//                     },
//                     '& .MuiOutlinedInput-input': { color: style.color4,
//                     '&:-webkit-autofill': { WebkitBoxShadow: '0 0 0 100px rgb(82, 109, 130) inset',
//                         WebkitTextFillColor: style.color4, color:style.color4
//                      }, }, 
//                 }}
//                 />
                

//                 </div>
//                 </h4>
//         </div>

//         <div style={{display:'flex', flex:1, marginBottom:'10px', width:'100%', backgroundColor:style.color1}}>     

//             <FormControl sx={{
//                   backgroundColor: style.color2,
//                   flex: 1,
//                   width:'100%',
//                   size: 'small',
//                   margin: 'auto',
//                   boxShadow:5,
                  

//                   '& .MuiInputBase-input': {
//                     color: style.color3,
//                     fontSize: '14pt',
//                     size: 'small',
//                   },
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: style.color1,
//                       borderWidth: '2px'
//                     },
//                     '&:hover fieldset': {
//                       borderColor: style.color2,
//                       borderWidth: '3px',
//                     },
//                     '&.Mui-focused fieldset': {
//                       borderColor: style.color2,
//                       borderWidth: '3px',
//                     },
//                     '&.Mui-disabled': {
//                       '& fieldset': {
//                         borderColor: style.color1,
//                       },
//                     },
//                   },
//                   '& .MuiInputLabel-root': {
//                     color: style.color3,
//                     fontSize: '14pt',
//                   },
//                   '& .MuiInputLabel-root.Mui-focused': {
//                     color: style.color3,
//                   },
//                   '& .MuiInputLabel-root.Mui-disabled': {
//                     color: style.color3,
//                   },
//                   '& .MuiInputBase-root.Mui-disabled': {
//                     '& input': {
//                       color: style.color3,
//                       WebkitTextFillColor: style.color1,
//                       fontSize: '14pt'
//                     },
//                   },
//                   '& .MuiOutlinedInput-input': {
//                     color: style.color4,
//                     '& input:-webkit-autofill': {
//                     WebkitBoxShadow: '0 0 0 100px rgb(82, 109, 130) inset !important',
//                     WebkitTextFillColor: `${style.color4} !important`,
//                     color: `${style.color4} !important`,
//                     caretColor: `${style.color4} !important`,
//                 },
//                   },
//                 }} variant="outlined">
//                   <InputLabel size="small" htmlFor="outlined-adornment-password">Password</InputLabel>
//                   <OutlinedInput
//                     id="outlined-adornment-password"
//                     type={showPassword ? 'text' : 'password'}
//                     value={data.password}
//                     size='small'
//                     onChange={(e) => setData({...data, password: e.target.value})}
//                     sx={{
//                         '& input:-webkit-autofill': {
//                         WebkitBoxShadow: '0 0 0 100px rgb(82, 109, 130) inset !important',
//                         WebkitTextFillColor: `${style.color4} !important`,
//                         color: `${style.color4} !important`,
//                         caretColor: `${style.color4} !important`,
                        
//                     },
//                     }}
//                     endAdornment={
//                       <InputAdornment position="end">
//                         <IconButton
//                           aria-label="toggle password visibility"
//                           onClick={handleClickShowPassword}
//                           onMouseDown={handleMouseDownPassword}
//                           edge="end"
//                           sx={{ color: style.color1 }}
//                         >
//                           {showPassword ? <VisibilityOff /> : <Visibility />}
//                         </IconButton>
//                       </InputAdornment>
//                     }
//                     label="Password"
//                   />
  
//                  </FormControl>
                          
//                         </div>
//                         <div >
//                           <Link to='/forgot-password' style={{fontSize:'.9em', marginTop:'10px', fontWeight:'bold', textAlign:'center', color:style.color4, margin:'auto', alignContent:'center'}}>
//                               Forgot Password?
//                         </Link>
//                           </div>
       

        

//         {(data.email && data.password ) &&(
//         <div style={{marginTop:'25px', marginBottom:'50px', display:'flex', flex:1}}>
//             <Button onClick={loginUser}  sx={{ my: 2, color: style.color4, display: 'block', backgroundColor: style.color1, filter: 'brightness(1.1)', boxShadow:5,fontSize: '14pt', width: '75%', minWidth: '250px', margin: 'auto', marginTop: '0px', borderColor: style.color2, borderWidth: '1px', borderStyle: 'solid', '&:hover': { backgroundColor: style.color2 }, }}>Login</Button>
//         </div>
//         )}
//         {(!data.email || !data.password ) &&(
//         <div style={{marginTop:'25px', marginBottom:'50px', display:'flex', flex:1}}>
//             <Button disabled onClick={loginUser} sx={{ my: 2, color:style.color2, display:'block', backgroundColor:style.color1, fontSize:'14pt', width:'75%', margin:'auto', marginTop:'0px', borderColor:style.color2, borderWidth:'1px', borderStyle:'solid', '&:hover': {backgroundColor: style.color4 }, }}><span style={{color:style.color3}}>Login</span></Button>
//         </div>
//         )} 

//         <div style={{marginBottom:'30px'}}>
//             <h4>Not yet a member? Join for free!</h4>

//             <Button size='small' onClick={navToRegister} sx={{ my: 2, color: style.color3, display: 'block', margin: 'auto', boxShadow:5,backgroundColor: style.color1, filter: 'brightness(1.1)', fontSize: '12pt', width: '75%', minWidth: '150px', margin: 'auto', marginTop: '0px', borderColor: style.color2, borderWidth: '1px', borderStyle: 'solid', '&:hover': { backgroundColor: style.color2 } }}>
//                <strong>Create Account</strong>
//             </Button>


//             </div>
//             <div style={{ flex:1, borderBottomLeftRadius:'15px', color:style.color5, marginBottom:'10px'}}>                 
//                     By using this website, you agree to our 
//                     <div>
//                         <Button size='small' sx={{ my: 2, color: style.color3, display: 'block', margin: 'auto', backgroundColor: style.color1, fontSize: '12pt', width: '100%', minWidth: '250px', margin: 'auto', marginTop: '0px', borderColor: style.color1, borderWidth: '2px', borderStyle: 'solid', '&:hover': { backgroundColor: style.color1, color:style.color4 } }} onClick={setModalOpen.bind(null, true)}><strong>terms and conditions</strong></Button>
//                     </div>
//                     </div>

//                     <div id='modal-root'>
//                     <TermsModal isOpen={isModalOpen} close={closeModal}>
//                         <Terms />
//                     </TermsModal>
//                     </div>
            
//                 </div>                




//     </div>

//     </div>

// </div>
// <div id='modal-root'>
// </div>


//         </>
//         </>         

//     )

    
// }

